import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { BrandingConfig, BrandingSources, BrandingContextValue, PLATFORM_DEFAULTS } from '@/types/branding';

const BrandingContext = createContext<BrandingContextValue | null>(null);

interface BrandingProviderProps {
  children: ReactNode;
}

export function BrandingProvider({ children }: BrandingProviderProps) {
  const { user, loading: authLoading } = useAuth();
  const [branding, setBranding] = useState<BrandingConfig>(PLATFORM_DEFAULTS);
  const [sources, setSources] = useState<BrandingSources>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Apply CSS variables to :root
  const applyCSSVariables = useCallback((config: BrandingConfig) => {
    const root = document.documentElement;
    
    // Convert hex to HSL for CSS variables
    const hexToHSL = (hex: string): string => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      if (!result) return hex;
      
      let r = parseInt(result[1], 16) / 255;
      let g = parseInt(result[2], 16) / 255;
      let b = parseInt(result[3], 16) / 255;
      
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h = 0;
      let s = 0;
      const l = (max + min) / 2;

      if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
          case g: h = ((b - r) / d + 2) / 6; break;
          case b: h = ((r - g) / d + 4) / 6; break;
        }
      }

      return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
    };

    // Apply branding colors as CSS variables
    root.style.setProperty('--branding-primary', hexToHSL(config.primary_color));
    root.style.setProperty('--branding-secondary', hexToHSL(config.secondary_color));
    root.style.setProperty('--branding-font-family', config.font_family);
  }, []);

  // Update favicon dynamically
  const updateFavicon = useCallback((faviconUrl: string | null) => {
    if (!faviconUrl) return;

    let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = faviconUrl;
  }, []);

  // Update document title
  const updateDocumentTitle = useCallback((poweredByText: string | null) => {
    if (poweredByText) {
      // Use powered_by_text to append to existing title or set as app name
      const baseTitle = 'Calie.ai';
      document.title = poweredByText.startsWith('Powered by') 
        ? baseTitle 
        : poweredByText;
    }
  }, []);

  // Fetch branding from edge function
  const fetchBranding = useCallback(async () => {
    if (!user) {
      // Not authenticated - use platform defaults
      setBranding(PLATFORM_DEFAULTS);
      setSources({});
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('Calie_resolveBranding', {
        method: 'GET',
      });

      if (fnError) {
        throw new Error(fnError.message || 'Failed to fetch branding');
      }

      if (data?.branding) {
        setBranding(data.branding);
        setSources(data._sources || {});
        applyCSSVariables(data.branding);
        updateFavicon(data.branding.favicon_url);
        updateDocumentTitle(data.branding.powered_by_text);
      }
    } catch (err) {
      console.error('Error fetching branding:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      // Fall back to platform defaults on error
      setBranding(PLATFORM_DEFAULTS);
      applyCSSVariables(PLATFORM_DEFAULTS);
    } finally {
      setIsLoading(false);
    }
  }, [user, applyCSSVariables, updateFavicon, updateDocumentTitle]);

  // Refresh function for manual re-fetch
  const refresh = useCallback(async () => {
    await fetchBranding();
  }, [fetchBranding]);

  // Fetch branding when auth state changes
  useEffect(() => {
    if (!authLoading) {
      fetchBranding();
    }
  }, [authLoading, fetchBranding]);

  // Apply platform defaults on initial mount
  useEffect(() => {
    applyCSSVariables(PLATFORM_DEFAULTS);
  }, [applyCSSVariables]);

  const value: BrandingContextValue = {
    branding,
    sources,
    isLoading,
    error,
    refresh,
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBrandingContext(): BrandingContextValue {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBrandingContext must be used within a BrandingProvider');
  }
  return context;
}
